/**
 * Java 8 CST (concrete syntax tree) mddel.
 */

import {
	AssignmentOrCompoundAssignment,
	Comment,
	IntegerLiteral,
	isPrimitive,
	Keyword,
	Misc,
	Operator,
	PrimitiveKw,
	Separator,
	Token,
	TokenIdentifier,
	TokenKeyword,
	TokenOfType,
	tokenTypeToString,
} from "./lexer";
import {cafebabeUnreachable, Span} from "./error";
import {ScopeChainNode} from "./analysis";
import {
	AccessorFlags,
	ClassDeclarationInfo,
	GeneralTypeOrAny,
	InterfaceDeclarationInfo,
	LabelInfo,
	LocalVariableDef,
	MemberFieldInfo,
	MethodDeclarationInfo,
	PackageInfo,
	TypeParameterInfo,
} from "./type_model";

const DEFAULT_SPAN = Object.freeze({start: -1, end: -1});

/**
 * Used for AST visualisation -- should not be seen by students.
 */
export type NodeInfo = {
	text: {name: string; title: string};
	children: NodeInfo[];
};

/**
 * Base class for all trees in the CST.
 */
export class BaseTree {
	public span: Span = DEFAULT_SPAN;
	public flags = 0;

	public docComment: Comment | null = null;

	public annotations: AnnotationTree[] = [];
	public scope: ScopeChainNode | null = null;

	public javaType: GeneralTypeOrAny | null = null;

	attachDocComment(docComment: Comment | null) {
		if (docComment) this.docComment = docComment;
		return this;
	}

	setScope(scope: ScopeChainNode) {
		this.scope = scope;
	}

	isTypeDeclaration(): this is
		| ClassDeclarationTree
		| InterfaceDeclarationTree
		| EnumDeclarationTree {
		return false;
	}

	/**
	 * Traverse all trees of this tree, calling the enter function before visiting children and the exit function after.
	 * If the enter function returns false, the tree will not be visited.
	 */
	visit(enter: (tree: BaseTree) => boolean, exit: (tree: BaseTree) => void) {
		if (!enter(this)) return;

		for (const key in this) {
			if (!this.hasOwnProperty(key)) continue;
			const o = this[key] as any;
			if (o instanceof BaseTree) {
				o.visit(enter, exit);
			} else if (Array.isArray(o)) {
				for (const v of o) {
					if (!(v instanceof BaseTree)) continue;
					v.visit(enter, exit);
				}
			}
		}

		exit(this);
	}

	*iterTokensAndTrees(): Generator<Token | BaseTree> {
		cafebabeUnreachable("iterTokensAndTrees: unimplemented");
	}

	/**
	 * Compute the span of the tree using its tokens.
	 */
	resolveSpans() {
		if (this.span.start !== -1) return; // already resolved

		let min = Number.MAX_SAFE_INTEGER;
		let max = Number.MIN_SAFE_INTEGER;

		for (const tokenOrTree of this.iterTokensAndTrees()) {
			if (tokenOrTree instanceof BaseTree) {
				tokenOrTree.resolveSpans();
			}

			min = Math.min(min, tokenOrTree.span.start);
			max = Math.max(max, tokenOrTree.span.end);
		}

		this.setSpan({start: min, end: max});
	}

	/**
	 * Given a cursor position, find the path of trees and tokens taking us to that cursor position.
	 */
	findChainAtPosition(pos: number): (BaseTree | Token)[] | null {
		if (this.span.start <= pos && this.span.end >= pos) {
			for (const tokenOrTree of this.iterTokensAndTrees()) {
				if (tokenOrTree instanceof BaseTree) {
					const result = tokenOrTree.findChainAtPosition(pos);
					if (result) return [this, ...result];
				} else if (
					tokenOrTree.span.start <= pos &&
					tokenOrTree.span.end >= pos
				) {
					return [this, tokenOrTree];
				}
			}
			return [this];
		}
		return null;
	}

	/**
	 * Given a callback, iterate over every chain of tokens and trees in the compilation unit. TODO
	 */
	iterChains(callback: (chain: Readonly<(BaseTree | Token)[]>) => void) {}

	/**
	 * Get the lowest-level tree in the list of trees or tokens.
	 * @param trees The list of trees and tokens.
	 * @return The tree, or null if no tree exists.
	 */
	static getLastTree(trees: (BaseTree | Token)[] | null): BaseTree | null {
		if (trees) {
			let l = trees.length - 1,
				e: BaseTree | Token | undefined;
			for (; l >= 0; --l) if ((e = trees[l]) instanceof BaseTree) return e;
		}
		return null;
	}

	visualNodeInfo(): NodeInfo {
		// Iterate through properties and add whichever ones are part of base tree,
		// or are arrays of base tree
		const children: NodeInfo[] = [];

		for (const key in this) {
			if (this.hasOwnProperty(key)) {
				const value = this[key];
				if (value instanceof BaseTree) {
					children.push(value.visualNodeInfo());
				} else if (Array.isArray(value)) {
					for (const v of value) {
						if (v instanceof BaseTree) {
							children.push(v.visualNodeInfo());
						}
					}
				}
			}
		}

		return {
			text: {name: this.constructor.name, title: this.prettyPrint()},
			children,
		};
	}

	/**
	 * Add annotations to this tree. (Since annotations can be placed nearly anywhere, we give up and just let anything
	 * be annotated. Not far from the truth!)
	 */
	attachAnnotations(annotations: AnnotationTree[]) {
		this.annotations = annotations;
		return this;
	}

	/**
	 * Directly set this tree's span.
	 */
	setSpan(span: Span) {
		this.span = span;
		return this;
	}

	setZw(start: number) {
		this.span = {start, end: start};
		return this;
	}

	/**
	 * Print the tree in a reasonably readable manner. It does not have to be isomorphic to the source.
	 */
	prettyPrint() {
		return "";
	}

	/**
	 * Stupid hack to parenthesize all expressions for operator precedence tests. Shouldn't be used in actual student=
	 * facing stuff.
	 */
	_exhaustivelyParenthesize() {
		for (const key in this)
			if (this.hasOwnProperty(key) && this[key] instanceof BaseTree)
				// @ts-ignore
				this[key].prettyPrint = Object.getPrototypeOf(
					this[key]
				)._exhaustivelyParenthesize.bind(this[key]);
		const result =
			"(" + Object.getPrototypeOf(this).prettyPrint.bind(this)() + ")";
		for (const key in this)
			if (this.hasOwnProperty(key) && this[key] instanceof BaseTree)
				// @ts-ignore
				this[key].prettyPrint = Object.getPrototypeOf(this[key]).prettyPrint;
		return result;
	}

	isTypeTree(): this is ArrayTypeTree | ReferenceTypeTree | PrimitiveTypeTree {
		return false;
	}
}

/**
 * Base class for all expressions.
 */
export class BaseExpressionTree extends BaseTree {}

/**
 * e.g. a = 0 OR b = f() OR c[] = f()   (deprecated syntax)
 */
export class LocalVariableDeclarationTree extends BaseTree {
	constructor(
		public name: IdentTree,
		public cStyleBrackets: BracketsTree | null,
		public equalsToken: TokenOfType<Operator.Eq> | null,
		public rhs: BaseExpressionTree | null
	) {
		super();
	}

	*iterTokensAndTrees(): Generator<Token | BaseTree> {
		if (this.name) yield this.name;
		if (this.cStyleBrackets) yield this.cStyleBrackets;
		if (this.equalsToken) yield this.equalsToken;
		if (this.rhs) yield this.rhs;
	}
}

/**
 * e.g. (x)
 */
export class ParenthesizedExpressionTree extends BaseExpressionTree {
	constructor(
		public openParen: Token,
		public contents: BaseExpressionTree | null,
		public closeParen: Token | null
	) {
		super();
	}

	*iterTokensAndTrees(): Generator<Token | BaseTree> {
		yield this.openParen;
		if (this.contents) yield this.contents;
		if (this.closeParen) yield this.closeParen;
	}

	prettyPrint() {
		return `(${this.contents?.prettyPrint()})`;
	}
}

/**
 * e.g.
 *
 *    decls[0]
 *     v
 * int a, b = 0;
 * ^^^
 * ty
 */
export class LocalVariableDeclarationListTree extends BaseTree {
	constructor(
		public ty: ReferenceTypeTree | PrimitiveTypeTree | ArrayTypeTree,
		public decls: LocalVariableDeclarationTree[],
		public commas: TokenOfType<Separator.Comma>[],
		public semi: TokenOfType<Separator.Semicolon> | null,
		public modifiers: ModifierTree[]
	) {
		super();
	}

	*iterTokensAndTrees(): Generator<Token | BaseTree> {
		if (this.ty) yield this.ty;
		for (const decl of this.decls) yield decl;
		for (const comma of this.commas) yield comma;
		if (this.semi) yield this.semi;
		for (const mod of this.modifiers) yield mod;
	}

	setModifiers(modifiers: ModifierTree[]) {
		this.modifiers = modifiers;
		return this;
	}
}

/**
 * e.g. cow[1]
 */
export class ArrayAccessTree extends BaseExpressionTree {
	constructor(
		public lhs: BaseExpressionTree,
		public openBracket: TokenOfType<Separator.LBracket>,
		public index: BaseExpressionTree | null,
		public closeBracket: TokenOfType<Separator.RBracket> | null
	) {
		super();
	}

	*iterTokensAndTrees(): Generator<Token | BaseTree> {
		yield this.lhs;
		yield this.openBracket;
		if (this.index) yield this.index;
		if (this.closeBracket) yield this.closeBracket;
	}

	prettyPrint() {
		return `${this.lhs?.prettyPrint()}[${this.index?.prettyPrint()}]`;
	}
}

/**
 * e.g. the expression a += b, as opposed to the statement
 */
export class AssignmentTree extends BaseExpressionTree {
	constructor(
		public lhs: BaseExpressionTree,
		public operator: AssignmentOrCompoundAssignment,
		public rhs: BaseExpressionTree | null
	) {
		super();
	}

	*iterTokensAndTrees(): Generator<Token | BaseTree> {
		yield this.lhs;
		yield this.operator;
		if (this.rhs) yield this.rhs;
	}

	prettyPrint() {
		return `${this.lhs?.prettyPrint()} ${tokenTypeToString(this.operator.type)} ${this.rhs?.prettyPrint()}`;
	}
}

/**
 * e.g. the statement a.cow();
 */
export class ExpressionStatementTree extends BaseTree {
	constructor(
		public expression: BaseExpressionTree,
		public semi: TokenOfType<Separator.Semicolon> | null
	) {
		super();
	}

	*iterTokensAndTrees(): Generator<Token | BaseTree> {
		yield this.expression;
		if (this.semi) yield this.semi;
	}
}

export type UnaryOperatorToken = TokenOfType<
	| Operator.Plus
	| Operator.Minus
	| Operator.Not
	| Operator.Tilde
	| Operator.PlusPlus
	| Operator.MinusMinus
>;

export class UnaryOperatorTree extends BaseExpressionTree {
	constructor(
		public token: UnaryOperatorToken,
		public operand: BaseExpressionTree | null,
		public isPrefix: boolean
	) {
		super();
	}

	*iterTokensAndTrees(): Generator<Token | BaseTree> {
		yield this.token;
		if (this.operand) yield this.operand;
	}

	get operatorTag():
		| Operator.Plus
		| Operator.Minus
		| Operator.Not
		| Operator.Tilde
		| Operator.PlusPlus
		| Operator.MinusMinus {
		return this.token.type;
	}

	prettyPrint() {
		if (this.isPrefix) {
			return `${tokenTypeToString(this.token.type)}${this.operand?.prettyPrint()}`;
		} else {
			return `${this.operand?.prettyPrint()}${tokenTypeToString(this.token.type)}`;
		}
	}
}

export class TypeParameterTree extends BaseTree {
	constructor(
		public name: IdentTree,
		public extendsToken: TokenOfType<Keyword.extends> | null,
		public extendsClass: ReferenceTypeTree | null,
		public additionalBounds: BoundsTree | null
	) {
		super();
	}

	referent: TypeParameterInfo | null = null;

	*iterTokensAndTrees(): Generator<Token | BaseTree> {
		yield this.name;
		if (this.extendsToken) yield this.extendsToken;
		if (this.extendsClass) yield this.extendsClass;
		if (this.additionalBounds) yield this.additionalBounds;
	}
}

export class TypeParametersTree extends BaseTree {
	constructor(
		public open: TokenOfType<Operator.Lt>,
		public typeParameters: TypeParameterTree[],
		public commas: TokenOfType<Separator.Comma>[],
		public close: TokenOfType<Operator.Gt> | null
	) {
		super();
	}

	*iterTokensAndTrees(): Generator<Token | BaseTree> {
		yield this.open;
		for (const param of this.typeParameters) yield param;
		for (const comma of this.commas) yield comma;
		if (this.close) yield this.close;
	}
}

export class TernaryOperatorTree extends BaseExpressionTree {
	constructor(
		public condition: BaseExpressionTree | null,
		public question: TokenOfType<Operator.Question>,
		public trueExpression: BaseExpressionTree | null,
		public colon: TokenOfType<Operator.Colon> | null,
		public falseExpression: BaseExpressionTree | null
	) {
		super();
	}

	prettyPrint() {
		return `${this.condition?.prettyPrint()} ? ${this.trueExpression?.prettyPrint()} : ${this.falseExpression?.prettyPrint()}`;
	}

	*iterTokensAndTrees(): Generator<Token | BaseTree> {
		if (this.condition) yield this.condition;
		yield this.question;
		if (this.trueExpression) yield this.trueExpression;
		if (this.colon) yield this.colon;
		if (this.falseExpression) yield this.falseExpression;
	}
}

export type BinaryOperator =
	| Operator.Plus
	| Operator.Minus
	| Operator.Star
	| Operator.Slash
	| Operator.Percent
	| Operator.Lt
	| Operator.Gt
	| Operator.LtEq
	| Operator.GtEq
	| Operator.EqEq
	| Operator.NotEq
	| Operator.AndAnd
	| Operator.OrOr
	| Operator.And
	| Operator.Or
	| Operator.Caret
	| Operator.LtLt
	| Operator.GtGt
	| Operator.GtGtGt
	| Operator.PlusEq
	| Operator.MinusEq
	| Operator.StarEq
	| Operator.SlashEq
	| Operator.PercentEq
	| Operator.AndEq
	| Operator.OrEq
	| Operator.CaretEq
	| Operator.LtLtEq
	| Operator.GtGtEq
	| Operator.GtGtGtEq
	| Keyword.instanceof;

/**
 * Binary operator (add, subtract, multiply, divide, etc).
 *
 * Illustration of all components:
 * left operator right
 * v     v         v
 * 1     +         2
 */
export class BinaryOperatorTree extends BaseExpressionTree {
	constructor(
		public operator: TokenOfType<BinaryOperator>,
		public left: BaseExpressionTree | null,
		public right:
			| BaseExpressionTree
			| ReferenceTypeTree /* for instanceof */
			| ArrayTypeTree
			| PrimitiveTypeTree
			| null
	) {
		super();
	}

	*iterTokensAndTrees(): Generator<Token | BaseTree> {
		if (this.left) yield this.left;
		yield this.operator;
		if (this.right) yield this.right;
	}

	prettyPrint() {
		return `${this.left?.prettyPrint()} ${tokenTypeToString(this.operator.type)} ${this.right?.prettyPrint()}`;
	}
}

/**
 * List of statements, found in for loops.
 *
 * Illustration of all components:
 *
 * statements[0]
 * vvvvv   v seps[0]
 * i = 0   ,     j += 10
 *              ^^^^^^^ statements[1]
 */
export class ExpressionStatementListTree extends BaseTree {
	constructor(
		public statements: ExpressionStatementTree[],
		public seps: TokenOfType<Separator.Comma>[]
	) {
		super();
	}

	*iterTokensAndTrees(): Generator<Token | BaseTree> {
		for (const stmt of this.statements) yield stmt;
		for (const sep of this.seps) yield sep;
	}
}

/**
 * Standard for loop.
 *
 * Illustration of all components:
 *
 * for forInit (optional)    sep2 forUpdate (optional)
 * vvv    vvvvvvvvv             v vvv  vvvvv statement
 * for  ( int i = 0;    i < 10  ; ++i) {   }
 *      ^          ^    ^^^^^^       ^ close
 *   open        sep1   forCond (optional)
 */
export class ForLoopTree extends BaseTree {
	constructor(
		public forToken: TokenOfType<Keyword.for>,
		public openParen: TokenOfType<Separator.LParen> | null,
		public forInit:
			| ErrorTree
			| ExpressionStatementListTree
			| LocalVariableDeclarationListTree
			| null,
		public sep1: TokenOfType<Separator.Semicolon> | null,
		public forCond: BaseExpressionTree | null,
		public sep2: TokenOfType<Separator.Semicolon> | null,
		public forUpdate: ExpressionStatementListTree | null,
		public closeParen: TokenOfType<Separator.RParen> | null,
		public statement: BaseTree | null
	) {
		super();
	}

	*iterTokensAndTrees(): Generator<Token | BaseTree> {
		yield this.forToken;
		if (this.openParen) yield this.openParen;
		if (this.forInit) yield this.forInit;
		if (this.sep1) yield this.sep1;
		if (this.forCond) yield this.forCond;
		if (this.sep2) yield this.sep2;
		if (this.forUpdate) yield this.forUpdate;
		if (this.closeParen) yield this.closeParen;
		if (this.statement) yield this.statement;
	}
}

/**
 * Enhanced for loop.
 *
 * Illustration of all components:
 *
 * for                varName iterated
 * vvv                vvvvv   vvvvvvvv
 * for (  final   int chick : chickens   ) { chick.cluck(); }
 *     ^          ^^^       ^            ^ ^^^^^^^^^^^^^^^^^^ statement
 *   open         elemType  colon    close
 */
export class EnhancedForLoopTree extends BaseTree {
	constructor(
		public forToken: TokenOfType<Keyword.for>,
		public openParen: TokenOfType<Separator.LParen> | null,
		public finalToken: TokenOfType<Keyword.final> | null,
		public elemType:
			| ReferenceTypeTree
			| PrimitiveTypeTree
			| ArrayTypeTree
			| null,
		public varName: IdentTree,
		public colon: TokenOfType<Operator.Colon> | null,
		public iterated: BaseExpressionTree | null,
		public closeParen: TokenOfType<Separator.RParen> | null,
		public statement: BaseTree | null
	) {
		super();
	}

	*iterTokensAndTrees(): Generator<Token | BaseTree> {
		if (this.forToken) yield this.forToken;
		if (this.openParen) yield this.openParen;
		if (this.finalToken) yield this.finalToken;
		if (this.elemType) yield this.elemType;
		yield this.varName;
		if (this.colon) yield this.colon;
		if (this.iterated) yield this.iterated;
		if (this.closeParen) yield this.closeParen;
		if (this.statement) yield this.statement;
	}
}

export class WhileLoopTree extends BaseTree {
	constructor(
		public whileToken: TokenOfType<Keyword.while>,
		public openParen: TokenOfType<Separator.LParen> | null,
		public condition: BaseExpressionTree | null,
		public closeParen: TokenOfType<Separator.RParen> | null,
		public statement: BaseTree | null
	) {
		super();
	}

	*iterTokensAndTrees(): Generator<Token | BaseTree> {
		yield this.whileToken;
		if (this.openParen) yield this.openParen;
		if (this.condition) yield this.condition;
		if (this.closeParen) yield this.closeParen;
		if (this.statement) yield this.statement;
	}
}

export class DoWhileLoopTree extends BaseTree {
	constructor(
		public doToken: TokenOfType<Keyword.do>,
		public statement: BaseTree | null,
		public whileToken: TokenOfType<Keyword.while> | null,
		public openParen: TokenOfType<Separator.LParen> | null,
		public condition: BaseExpressionTree | null,
		public closeParen: TokenOfType<Separator.RParen> | null,
		public semi: TokenOfType<Separator.Semicolon> | null
	) {
		super();
	}

	*iterTokensAndTrees(): Generator<Token | BaseTree> {
		yield this.doToken;
		if (this.statement) yield this.statement;
		if (this.whileToken) yield this.whileToken;
		if (this.openParen) yield this.openParen;
		if (this.condition) yield this.condition;
		if (this.closeParen) yield this.closeParen;
		if (this.semi) yield this.semi;
	}
}

/**
 * Lambda expression.
 *
 * Illustration of all components:
 *
 * parens.start
 * v          v args[1]   v parens.end
 * (   a    , b           ) ->
 *     ^    ^ commas[0]     ^^ arrow
 *   args[0]
 */
export class LambdaExpressionTree extends BaseExpressionTree {
	constructor(
		public args: FunctionArgumentTree[],
		public openParen: TokenOfType<Separator.LParen> | null,
		public closeParen: TokenOfType<Separator.RParen> | null,
		public arrow: TokenOfType<Operator.Arrow>,
		public body: BlockTree | BaseExpressionTree | null
	) {
		super();
	}

	*iterTokensAndTrees(): Generator<Token | BaseTree> {
		if (this.openParen) yield this.openParen;
		for (const arg of this.args) yield arg;
		if (this.closeParen) yield this.closeParen;
		yield this.arrow;
		if (this.body) yield this.body;
	}

	prettyPrint(): string {
		return `(${this.args.map((x) => x.prettyPrint()).join(", ")}) -> ${this.body?.prettyPrint()}`;
	}
}

export class PackageDeclarationTree extends BaseTree {
	constructor(
		public name: NameTree | IdentTree | null,
		public packageToken: TokenOfType<Keyword.package>,
		public semi: TokenOfType<Separator.Semicolon> | null
	) {
		super();
	}

	referent: PackageInfo | null = null;

	*iterTokensAndTrees(): Generator<Token | BaseTree> {
		if (this.name) yield this.name;
		yield this.packageToken;
		if (this.semi) yield this.semi;
	}

	prettyPrint() {
		return `package ${this.name?.prettyPrint()};`;
	}
}

export class CompilationUnitTree extends BaseTree {
	constructor(
		public packageDeclaration: PackageDeclarationTree | null,
		public imports: ImportDeclarationTree[],
		public typeDeclarations: BaseTree[]
	) {
		super();
	}

	*iterTokensAndTrees(): Generator<Token | BaseTree> {
		if (this.packageDeclaration) yield this.packageDeclaration;
		for (const imp of this.imports) yield imp;
		for (const td of this.typeDeclarations) yield td;
	}

	/**
	 * Get the package of this compilation unit as a raw string, or "" if the compilation unit is at the top level.
	 *
	 * This may be malformed, e.g. "java.util."
	 */
	getPackageUnchecked(): string {
		return this.packageDeclaration?.name?.toIdentifierString() ?? "";
	}

	getPackage(): PackageInfo | null {
		return this.packageDeclaration?.referent ?? null;
	}

	/**
	 * Get the binary names of all interfaces, classes and enums, including nested classes, but not including
	 * anonymous classes.
	 */
	getFullyQualifiedTypeNames(): Map<string, number /* flags */> {
		const result = new Map<string, number>();

		function processDeclaration(tr: BaseTree, baseName: string) {
			if (
				!(tr instanceof ClassDeclarationTree) &&
				!(tr instanceof InterfaceDeclarationTree) &&
				!(tr instanceof EnumDeclarationTree)
			)
				return;

			const name = tr.name?.identifier; // name not available
			if (!name) return;

			const base = (baseName ? baseName + "." : baseName) + name;
			tr.body?.forEach((decl) => processDeclaration(decl, base)); // process inner classes
			result.set(base, modifiersToAccessorFlags(tr.modifiers));
		}

		const pkg = this.getPackageUnchecked();
		this.typeDeclarations.forEach((decl) => processDeclaration(decl, pkg));
		return result;
	}
}

export class IdentTree extends BaseExpressionTree {
	constructor(
		public token: TokenIdentifier,
		public annotations: AnnotationTree[] = []
	) {
		super();
		this.span = this.token.span;
	}

	get identifier() {
		return this.token.value;
	}

	get name() {
		return this;
	}

	public referent:
		| PackageInfo
		| ClassDeclarationInfo
		| InterfaceDeclarationInfo
		| TypeParameterInfo
		| MethodDeclarationInfo
		| MemberFieldInfo
		| LocalVariableDef
		| LabelInfo
		| null = null;

	*iterTokensAndTrees(): Generator<Token | BaseTree> {
		yield this.token;
		for (const ann of this.annotations) yield ann;
	}

	toIdentifierString(): string {
		return this.identifier;
	}

	prettyPrint(): string {
		return this.identifier;
	}

	static zw(end: number) {
		return new IdentTree({
			type: Misc.identifier,
			value: "",
			span: {start: end, end},
		}).setSpan({start: end, end});
	}
}

/**
 * A name tree is a name that may contain dots, e.g. "java.util", as opposed to an IdentTree which contains only a
 * single identifier. Name trees appear in all kinds of places: type names,
 */
export class NameTree extends BaseExpressionTree {
	constructor(
		public dotToken: Token,
		public qualifier: NameTree | IdentTree,
		public identifier: IdentTree
	) {
		super();
	}

	public referent:
		| PackageInfo /* package */
		| ClassDeclarationInfo
		| InterfaceDeclarationInfo
		| MemberFieldInfo
		| null = null;

	*iterTokensAndTrees(): Generator<Token | BaseTree> {
		yield this.qualifier;
		yield this.dotToken;
		yield this.identifier;
	}

	/**
	 * e.g. "java.util", "java.util.". Because we allow partial qualified identifiers you should check the result for
	 * whether it is valid.
	 */
	toIdentifierString(): string {
		return (
			this.qualifier.toIdentifierString() +
			"." +
			this.identifier.toIdentifierString()
		);
	}

	prettyPrint(): string {
		return this.qualifier.prettyPrint() + "." + this.identifier.prettyPrint();
	}
}

/**
 * e.g. @NotNull
 */
export class AnnotationTree extends BaseTree {
	constructor(
		public atToken: TokenOfType<Separator.At>,
		public name: NameTree | IdentTree,
		public open: TokenOfType<Separator.LParen> | null,
		public close: TokenOfType<Separator.RParen> | null,
		public args: BaseTree | null
	) {
		super();
	}

	*iterTokensAndTrees(): Generator<Token | BaseTree> {
		yield this.atToken;
		yield this.name;
		if (this.open) yield this.open;
		if (this.args) yield this.args;
		if (this.close) yield this.close;
	}
}

/**
 * e.g. new ArrayList<>()
 */
export class ClassCreationTree extends BaseExpressionTree {
	constructor(
		public newToken: TokenOfType<Keyword.new>,
		public type: ReferenceTypeTree | PrimitiveTypeTree | ArrayTypeTree | null,
		public typeArgs: TypeArgumentsTree | null,
		public args: BaseExpressionTree[] | null,
		public commas: Token[],
		public openParen: TokenOfType<Separator.LParen> | null,
		public closeParen: TokenOfType<Separator.RParen> | null
	) {
		super();
	}

	prettyPrint() {
		return `new ${this.type?.prettyPrint()}(${this.args?.map((x) => x.prettyPrint()).join(", ")})`;
	}

	*iterTokensAndTrees(): Generator<Token | BaseTree> {
		yield this.newToken;
		if (this.type) yield this.type;
		if (this.typeArgs) yield this.typeArgs;
		if (this.openParen) yield this.openParen;
		if (this.args) for (const arg of this.args) yield arg;
		if (this.closeParen) yield this.closeParen;
	}
}

/**
 * Tree representing an array creation expression, e.g. new int[] { 1, 2, 3 }.
 */
export class ArrayCreationTree extends BaseExpressionTree {
	constructor(
		public newToken: TokenOfType<Keyword.new>,
		public type: ReferenceTypeTree | PrimitiveTypeTree | ArrayTypeTree,
		public dimsBrackets: TokenOfType<Separator.LBracket | Separator.RBracket>[],
		public dims: BaseExpressionTree[],
		public initializer: ArrayInitializerTree | null
	) {
		super();
		this.dimensions = dimsBrackets.reduce(
			(acc, val) => acc + +(val.type === Separator.LBracket),
			0
		);
	}

	dimensions: number;

	*iterTokensAndTrees(): Generator<Token | BaseTree> {
		yield this.newToken;
		yield this.type;
		if (this.dimsBrackets) for (const dim of this.dimsBrackets) yield dim;
		if (this.dims) for (const dim of this.dims) yield dim;
		if (this.initializer) yield this.initializer;
	}
}

/**
 * Tree representing a sequence of erroneous tokens.
 */
export class ErrorTree extends BaseExpressionTree {
	constructor(public elements: (BaseTree | Token)[]) {
		super();
	}

	*iterTokensAndTrees(): Generator<Token | BaseTree> {
		for (const elem of this.elements) yield elem;
	}
}

/**
 *         v dotToken
 * e.g. f().a
 * 	    ^^^ ^ rhs
 * 	    lhs
 */
export class FieldAccessTree extends BaseExpressionTree {
	constructor(
		public lhs: BaseExpressionTree,
		public dotToken: Token,
		public rhs: IdentTree | ThisTree | SuperTree | ClassCreationTree | null
	) {
		super();
	}

	*iterTokensAndTrees(): Generator<Token | BaseTree> {
		yield this.lhs;
		yield this.dotToken;
		if (this.rhs) yield this.rhs;
	}

	prettyPrint() {
		return `${this.lhs?.prettyPrint()}.${this.rhs?.prettyPrint()}`;
	}
}

/**
 * e.g. int.class, int[].class
 */
export class ClassLiteralTree extends BaseExpressionTree {
	constructor(
		public type:
			| ArrayTypeTree
			| PrimitiveTypeTree
			| ReferenceTypeTree
			| IdentTree,
		public dotToken: Token,
		public classToken: Token | null
	) {
		super();
	}

	*iterTokensAndTrees(): Generator<Token | BaseTree> {
		yield this.type;
		yield this.dotToken;
		if (this.classToken) yield this.classToken;
	}

	prettyPrint() {
		return `${this.type.prettyPrint()}.class`;
	}
}

/**
 * Import statement.
 *
 * Visualisation:
 *
 *   importToken             v starToken
 *   vvvvvv                   v semi
 *   import java.util.Random.*;
 *          ^^^^^^^^^^^^^^^^^ name
 *
 *   importToken
 *   vvvvvv                         v semi
 *   import static java.util.Random.;
 *          ^^^^^^ ^^^^^^^^^^^^^^^^^ name
 *        staticToken
 */
export class ImportDeclarationTree extends BaseTree {
	public isStatic: boolean;
	public isStarImport: boolean;

	constructor(
		public name: NameTree | IdentTree,
		public importToken: TokenOfType<Keyword.import>,
		public staticToken: TokenOfType<Keyword.static> | null,
		public semi: TokenOfType<Separator.Semicolon> | null,
		public starToken: TokenOfType<Operator.Star> | null
	) {
		super();
		this.isStatic = !!staticToken;
		this.isStarImport = !!starToken;
	}

	referent:
		| ClassDeclarationInfo
		| InterfaceDeclarationInfo
		| PackageInfo
		| null = null;

	*iterTokensAndTrees(): Generator<Token | BaseTree> {
		yield this.importToken;
		if (this.staticToken) yield this.staticToken;
		yield this.name;
		if (this.starToken) yield this.starToken;
		if (this.semi) yield this.semi;
	}
}

export class ModifierTree extends BaseTree {
	constructor(
		public token: Token,
		public modifier: string
	) {
		super();
	}

	*iterTokensAndTrees(): Generator<Token | BaseTree> {
		yield this.token;
	}
}

export function modifiersToAccessorFlags(modifiers: ModifierTree[]): number {
	const f = modifiers
		.map(({modifier}) => {
			switch (modifier) {
				case "abstract":
				case "private":
				case "public":
				case "final":
				case "protected":
				case "static":
					return AccessorFlags[modifier];
			}
			return 0;
		})
		.reduce((acc, val) => acc | val, 0);

	return f;
}

export class ClassDeclarationTree extends BaseTree {
	constructor(
		public modifiers: ModifierTree[],
		public classToken: TokenOfType<Keyword.class>,
		public name: IdentTree | null,
		public typeargs: TypeParametersTree | null,
		public extendsClause: ReferenceTypeTree | null,
		public implementsClause: ReferenceTypeTree[] | null,
		public openBrace: TokenOfType<Separator.LBrace> | null,
		public body: BaseTree[] | null,
		public closeBrace: TokenOfType<Separator.RBrace> | null
	) {
		super();
	}

	referent: ClassDeclarationInfo | null = null;

	attachModifiers(modifiers: ModifierTree[]) {
		this.modifiers.push(...modifiers);
		return this;
	}

	isTypeDeclaration(): this is ClassDeclarationTree {
		return true;
	}

	/**
	 * Return the set of accessor flags associated with this class, including implicit flags.
	 */
	getAccessorFlags(): AccessorFlags {
		return modifiersToAccessorFlags(this.modifiers);
	}

	*iterTokensAndTrees(): Generator<Token | BaseTree> {
		for (const mod of this.modifiers) yield mod;
		if (this.classToken) yield this.classToken;
		if (this.name) yield this.name;
		if (this.typeargs) yield this.typeargs;
		if (this.extendsClause) yield this.extendsClause;
		if (this.implementsClause) for (const ic of this.implementsClause) yield ic;
		if (this.openBrace) yield this.openBrace;
		if (this.body) for (const b of this.body) yield b;
		if (this.closeBrace) yield this.closeBrace;
	}
}

export class TryStatementTree extends BaseTree {
	constructor(
		public tryToken: Token,
		public resources: (LocalVariableDeclarationListTree | IdentTree)[] | null,
		public block: BlockTree | null,
		public catches: CatchStatementTree[],
		public finallyBlock: FinallyStatementTree | null
	) {
		super();
	}

	*iterTokensAndTrees(): Generator<Token | BaseTree> {
		yield this.tryToken;
		if (this.resources) for (const res of this.resources) yield res;
		if (this.block) yield this.block;
		for (const c of this.catches) yield c;
		if (this.finallyBlock) yield this.finallyBlock;
	}
}

export class CatchStatementTree extends BaseTree {
	constructor(
		public catchToken: Token,
		public open: TokenOfType<Separator.LParen> | null,
		public finalToken: TokenOfType<Keyword.final> | null,
		public exceptionType: ReferenceTypeTree[] | null,
		public exceptionName: IdentTree | null,
		public close: TokenOfType<Separator.RParen> | null,
		public block: BlockTree | null
	) {
		super();
	}

	*iterTokensAndTrees(): Generator<Token | BaseTree> {
		yield this.catchToken;
		if (this.open) yield this.open;
		if (this.finalToken) yield this.finalToken;
		if (this.exceptionType) for (const et of this.exceptionType) yield et;
		if (this.exceptionName) yield this.exceptionName;
		if (this.close) yield this.close;
		if (this.block) yield this.block;
	}
}

/**
 * "finally" part of a try/catch/finally statement.
 *
 * Illustration of all components:
 *
 * finallyToken
 * vvvvvvv
 * finally { ... }
 *         ^^^^^^^ block
 */
export class FinallyStatementTree extends BaseTree {
	constructor(
		public finallyToken: TokenOfType<Keyword.finally>,
		public block: BlockTree | null
	) {
		super();
	}

	*iterTokensAndTrees(): Generator<Token | BaseTree> {
		yield this.finallyToken;
		if (this.block) yield this.block;
	}

	prettyPrint() {
		return "finally";
	}
}

/**
 * Right hand side of an array initialisation, e.g. {1, 2, 3}
 */
export class ArrayInitializerTree extends BaseExpressionTree {
	constructor(
		public openBrace: TokenOfType<Separator.LBrace>,
		public elements: BaseExpressionTree[],
		public commas: TokenOfType<Separator.Comma>[],
		public closeBrace: TokenOfType<Separator.RBrace> | null
	) {
		super();
	}

	*iterTokensAndTrees(): Generator<Token | BaseTree> {
		yield this.openBrace;
		for (const elem of this.elements) yield elem;
		for (const comma of this.commas) yield comma;
		if (this.closeBrace) yield this.closeBrace;
	}
}

export class EnumDeclarationTree extends BaseTree {
	constructor(
		public modifiers: ModifierTree[],
		public openBrace: TokenOfType<Separator.LBrace> | null,
		public body: BaseTree[],
		public closeBrace: TokenOfType<Separator.RBrace> | null,
		public enumToken: TokenOfType<Keyword.enum>,
		public name: IdentTree | null,
		public superinterfaces: ReferenceTypeTree[] | null,
		public constants: EnumConstantTree[] | null
	) {
		super();
	}

	referent: ClassDeclarationInfo | InterfaceDeclarationInfo | null = null;

	isTypeDeclaration(): this is EnumDeclarationTree {
		return true;
	}

	*iterTokensAndTrees(): Generator<Token | BaseTree> {
		for (const mod of this.modifiers) yield mod;
		if (this.openBrace) yield this.openBrace;
		if (this.enumToken) yield this.enumToken;
		if (this.closeBrace) yield this.closeBrace;
		if (this.name) yield this.name;
		if (this.superinterfaces) for (const si of this.superinterfaces) yield si;
		if (this.constants) for (const con of this.constants) yield con;
		for (const b of this.body) yield b;
	}

	attachModifiers(modifiers: ModifierTree[]) {
		this.modifiers.push(...modifiers);
		return this;
	}
}

export class InterfaceDeclarationTree extends BaseTree {
	constructor(
		public modifiers: ModifierTree[],
		public interfaceToken: TokenOfType<Keyword.interface>,
		public name: IdentTree | null,
		public openBrace: TokenOfType<Separator.LBrace> | null,
		public body: BaseTree[] | null,
		public closeBrace: TokenOfType<Separator.RBrace> | null,
		public typeargs: TypeParametersTree | null,
		public extendsClause: ReferenceTypeTree[] | null
	) {
		super();
	}

	referent: InterfaceDeclarationInfo | null = null;

	isTypeDeclaration(): this is InterfaceDeclarationTree {
		return true;
	}

	*iterTokensAndTrees(): Generator<Token | BaseTree> {
		for (const mod of this.modifiers) yield mod;
		if (this.interfaceToken) yield this.interfaceToken;
		if (this.name) yield this.name;
		if (this.typeargs) yield this.typeargs;
		if (this.extendsClause) for (const ext of this.extendsClause) yield ext;
		if (this.openBrace) yield this.openBrace;
		if (this.body) for (const b of this.body) yield b;
		if (this.closeBrace) yield this.closeBrace;
	}

	attachModifiers(modifiers: ModifierTree[]) {
		this.modifiers.push(...modifiers);
		return this;
	}
}

export class CaseStatementTree extends BaseTree {
	public isDefault: boolean;

	constructor(
		public caseOrDefault: TokenOfType<Keyword.case | Keyword.default>,
		public expression: BaseExpressionTree | null,
		public colon: TokenOfType<Operator.Colon> | null,
		public statements: BaseTree[]
	) {
		super();
		this.isDefault = caseOrDefault.type === Keyword.default;
	}

	*iterTokensAndTrees(): Generator<Token | BaseTree> {
		yield this.caseOrDefault;
		if (this.expression) yield this.expression;
		if (this.colon) yield this.colon;
		for (const stmt of this.statements) yield stmt;
	}
}

/**
 * Switch statement:
 *
 * switchToken openParen   switchExpr  closeParen
 * vvvvvv        V            VVV      v
 * switch        (            cow      )    {  <- openBrace
 *    case 2:    <- components[0]
 *    return 1;  <- components[1]
 * } <- closeBrace
 */
export class SwitchStatementTree extends BaseTree {
	constructor(
		public switchToken: TokenOfType<Keyword.switch>,
		public openParen: TokenOfType<Separator.LParen> | null,
		public switchExpr: BaseExpressionTree | null,
		public closeParen: TokenOfType<Separator.RParen> | null,
		public openBrace: TokenOfType<Separator.LBrace> | null,
		public components: CaseStatementTree[],
		public closeBrace: TokenOfType<Separator.RBrace> | null
	) {
		super();
	}

	*iterTokensAndTrees(): Generator<Token | BaseTree> {
		yield this.switchToken;
		if (this.openParen) yield this.openParen;
		if (this.switchExpr) yield this.switchExpr;
		if (this.closeParen) yield this.closeParen;
		if (this.openBrace) yield this.openBrace;
		for (const comp of this.components) yield comp;
		if (this.closeBrace) yield this.closeBrace;
	}
}

export class EnumConstantTree extends BaseTree {
	constructor(
		public name: TokenIdentifier,
		public args: BaseExpressionTree[] | null,
		public body: BaseTree[] | null
	) {
		super();
	}

	referent: MemberFieldInfo | null = null;

	*iterTokensAndTrees(): Generator<Token | BaseTree> {
		yield this.name;
		if (this.args) for (const arg of this.args) yield arg;
		if (this.body) for (const b of this.body) yield b;
	}
}

export class FunctionArgumentsTree extends BaseTree {
	constructor(
		public openParen: Token,
		public args: FunctionArgumentTree[],
		public commas: Token[],
		public closeParen: Token | null,
		public cStyleArrayDecl: BracketsTree | null,
		public throwsClause: ThrowsTree | null
	) {
		super();
	}

	*iterTokensAndTrees(): Generator<Token | BaseTree> {
		yield this.openParen;
		for (const arg of this.args) yield arg;
		for (const comma of this.commas) yield comma;
		if (this.closeParen) yield this.closeParen;
		if (this.cStyleArrayDecl) yield this.cStyleArrayDecl;
		if (this.throwsClause) yield this.throwsClause;
	}
}

export class FunctionArgumentTree extends BaseTree {
	constructor(
		public type:
			| ReferenceTypeTree
			| PrimitiveTypeTree
			| ArrayTypeTree
			| null /* null in e.g. some lambdas */,
		public finalToken: Token | null,
		public name: IdentTree | null,
		public brackets: BracketsTree | null,
		public ellipsis: Token | null
	) {
		super();
	}

	*iterTokensAndTrees(): Generator<Token | BaseTree> {
		if (this.type) yield this.type;
		if (this.finalToken) yield this.finalToken;
		if (this.name) yield this.name;
		if (this.brackets) yield this.brackets;
		if (this.ellipsis) yield this.ellipsis;
	}

	get isFinal() {
		return this.finalToken !== null;
	}

	prettyPrint(): string {
		return `${this.type?.prettyPrint()} ${this.name?.prettyPrint()}`;
	}
}

export class ThrowsTree extends BaseTree {
	constructor(
		public throwsToken: TokenOfType<Keyword.throws>,
		public types: ReferenceTypeTree[]
	) {
		super();
	}

	*iterTokensAndTrees(): Generator<Token | BaseTree> {
		yield this.throwsToken;
		for (const type of this.types) yield type;
	}

	prettyPrint() {
		return `throws ${this.types.map((x) => x.prettyPrint()).join(", ")}`;
	}
}

export class FunctionDeclarationTree extends BaseTree {
	constructor(
		public modifiers: ModifierTree[],
		public body: BlockTree | null,
		public typeParams: TypeParametersTree | null,
		public returnType:
			| ReferenceTypeTree
			| ArrayTypeTree
			| PrimitiveTypeTree
			| null,
		public name: TokenIdentifier,
		public args: FunctionArgumentsTree | null,
		public isCtor: boolean
	) {
		super();
	}

	referent: MethodDeclarationInfo | null = null;

	*iterTokensAndTrees(): Generator<Token | BaseTree> {
		for (const mod of this.modifiers) yield mod;
		if (this.returnType) yield this.returnType;
		yield this.name;
		if (this.args) yield this.args;
		if (this.body) yield this.body;
	}

	setModifiers(modifiers: ModifierTree[]) {
		this.modifiers = modifiers;
		return this;
	}
}

/**
 * Sequence of brackets in a one-dimensional or multi-dimensional array, e.g. [][][] or [][].
 * Any one of these brackets may be annotated, for some reason.
 */
export class BracketsTree extends BaseTree {
	// e.g. for @chicken [] @ox [] [] this will be [[chicken], [ox], []]
	public perBracketAnnotations: AnnotationTree[][];

	constructor(
		annotations: AnnotationTree[][],
		public bracketPairs: {open: Token | null; close: Token | null}[]
	) {
		super();
		this.perBracketAnnotations = annotations;
	}

	*iterTokensAndTrees(): Generator<Token | BaseTree> {
		for (const pair of this.bracketPairs) {
			if (pair.open) yield pair.open;
			if (pair.close) yield pair.close;
		}
	}

	get count() {
		return this.bracketPairs.length;
	}

	prettyPrint() {
		return "[]".repeat(this.count);
	}
}

export class BlockTree extends BaseTree {
	constructor(
		public statements: BaseTree[],
		public openBrace: TokenOfType<Separator.LBrace>,
		public closeBrace: TokenOfType<Separator.RBrace> | null
	) {
		super();
	}

	*iterTokensAndTrees(): Generator<Token | BaseTree> {
		yield this.openBrace;
		for (const stmt of this.statements) yield stmt;
		if (this.closeBrace) yield this.closeBrace;
	}
}

export class StaticInitializationBlockTree extends BaseTree {
	constructor(
		public block: BlockTree,
		public staticToken: TokenOfType<Keyword.static>
	) {
		super();
	}

	*iterTokensAndTrees(): Generator<Token | BaseTree> {
		yield this.staticToken;
		yield this.block;
	}
}

/**
 * Represents a primitive type.
 */
export class PrimitiveTypeTree extends BaseTree {
	public type: PrimitiveKw | Keyword.void;

	constructor(public token: TokenOfType<PrimitiveKw>) {
		super();
		if (!isPrimitive(token.type) && token.type !== Keyword.void)
			throw new Error("PrimitiveTypeTree precondition");

		this.type = token.type;
	}

	*iterTokensAndTrees(): Generator<Token | BaseTree> {
		yield this.token;
	}

	prettyPrint() {
		return tokenTypeToString(this.type);
	}

	isTypeTree(): this is ReferenceTypeTree | PrimitiveTypeTree | ArrayTypeTree {
		return true;
	}
}

export class NullLiteralTree extends BaseExpressionTree {
	constructor(public token: TokenOfType<Misc.null>) {
		super();
	}

	*iterTokensAndTrees(): Generator<Token | BaseTree> {
		yield this.token;
	}

	prettyPrint() {
		return "null";
	}
}

export class ReturnStatementTree extends BaseTree {
	constructor(
		public returnToken: Token,
		public expression: BaseExpressionTree | null,
		public semi: Token | null
	) {
		super();
	}

	*iterTokensAndTrees(): Generator<Token | BaseTree> {
		yield this.returnToken;
		if (this.expression) yield this.expression;
		if (this.semi) yield this.semi;
	}
}

/**
 * Break statement.
 *
 * Illustration of all components:
 * breakToken
 * vvvvv     v semi
 * break  cow;
 *        ^^^ label
 */
export class BreakStatementTree extends BaseTree {
	constructor(
		public breakToken: Token,
		public label: IdentTree | null,
		public semi: TokenOfType<Separator.Semicolon> | null
	) {
		super();
	}

	*iterTokensAndTrees(): Generator<Token | BaseTree> {
		yield this.breakToken;
		if (this.label) yield this.label;
		if (this.semi) yield this.semi;
	}

	/**
	 * Whether this break statement has a label.
	 */
	hasLabel() {
		return this.label !== null;
	}
}

export class ContinueStatementTree extends BaseTree {
	constructor(
		public continueToken: Token,
		public label: IdentTree | null,
		public semi: TokenOfType<Separator.Semicolon> | null
	) {
		super();
	}

	*iterTokensAndTrees(): Generator<Token | BaseTree> {
		yield this.continueToken;
		if (this.label) yield this.label;
		if (this.semi) yield this.semi;
	}

	/**
	 * Whether this continue statement has a label.
	 */
	hasLabel() {
		return this.label !== null;
	}
}

export class ThrowStatementTree extends BaseTree {
	constructor(
		public throwToken: TokenOfType<Keyword.throw>,
		public expression: BaseExpressionTree | null,
		public semi: TokenOfType<Separator.Semicolon> | null
	) {
		super();
	}

	*iterTokensAndTrees(): Generator<Token | BaseTree> {
		yield this.throwToken;
		if (this.expression) yield this.expression;
		if (this.semi) yield this.semi;
	}
}

export class SynchronizedStatementTree extends BaseTree {
	constructor(
		public synchronizedToken: TokenOfType<Keyword.synchronized>,
		public openParen: TokenOfType<Separator.LParen> | null,
		public expr: BaseExpressionTree | null,
		public closeParen: TokenOfType<Separator.RParen> | null,
		public block: BlockTree | null
	) {
		super();
	}

	*iterTokensAndTrees(): Generator<Token | BaseTree> {
		yield this.synchronizedToken;
		if (this.openParen) yield this.openParen;
		if (this.expr) yield this.expr;
		if (this.closeParen) yield this.closeParen;
		if (this.block) yield this.block;
	}
}

export class LabelledStatementTree extends BaseTree {
	constructor(
		public label: IdentTree,
		public colon: TokenOfType<Operator.Colon> | null,
		public statement: BaseTree | null
	) {
		super();
	}

	*iterTokensAndTrees(): Generator<Token | BaseTree> {
		yield this.label;
		if (this.colon) yield this.colon;
		if (this.statement) yield this.statement;
	}
}

/**
 * e.g. & Cow & Chicken
 */
export class BoundsTree extends BaseTree {
	constructor(
		public ampersands: Token[],
		public types: ReferenceTypeTree[]
	) {
		super();
	}

	*iterTokensAndTrees(): Generator<Token | BaseTree> {
		for (const amp of this.ampersands) yield amp;
		for (const type of this.types) yield type;
	}
}

export class BooleanLiteralTree extends BaseExpressionTree {
	constructor(public token: TokenOfType<Misc.booleanLiteral>) {
		super();
	}

	*iterTokensAndTrees(): Generator<Token | BaseTree> {
		yield this.token;
	}

	get value(): boolean {
		return this.token.value;
	}

	isAlwaysTrueOrFalse() {
		return this.value;
	}

	prettyPrint() {
		return String(this.token.value);
	}
}

export class StringLiteralTree extends BaseExpressionTree {
	constructor(public token: TokenOfType<Misc.string>) {
		super();
	}

	*iterTokensAndTrees(): Generator<Token | BaseTree> {
		yield this.token;
	}

	prettyPrint() {
		return this.token.value;
	}
}

export class IntegerLiteralTree extends BaseExpressionTree {
	constructor(public token: IntegerLiteral) {
		super();
	}

	*iterTokensAndTrees(): Generator<Token | BaseTree> {
		yield this.token;
	}

	prettyPrint() {
		return Number(this.token.value) + (this.token.isLong ? "L" : "");
	}

	get isLong() {
		return this.token.isLong;
	}

	get valueNumber(): number {
		return Number(this.token.value);
	}

	get valueBigInt(): bigint {
		return BigInt(this.token.value);
	}
}

export class FloatLiteralTree extends BaseExpressionTree {
	constructor(public token: TokenOfType<Misc.floatLiteral>) {
		super();
	}

	*iterTokensAndTrees(): Generator<Token | BaseTree> {
		yield this.token;
	}

	isF32() {
		return this.token.isF32;
	}

	get value() {
		return this.token.value;
	}

	prettyPrint() {
		return String(this.token.value);
	}
}

export class CharacterLiteralTree extends BaseExpressionTree {
	constructor(public token: TokenOfType<Misc.characterLiteral>) {
		super();
	}

	*iterTokensAndTrees(): Generator<Token | BaseTree> {
		yield this.token;
	}

	get value(): number {
		return this.token.value.charCodeAt(0);
	}

	prettyPrint(): string {
		return "'" + this.token.value + "'";
	}
}

export class ThisTree extends BaseExpressionTree {
	constructor(public token: TokenOfType<Keyword.this>) {
		super();
	}

	referent: ClassDeclarationInfo | InterfaceDeclarationInfo | null = null;

	*iterTokensAndTrees(): Generator<Token | BaseTree> {
		yield this.token;
	}

	prettyPrint() {
		return "this";
	}
}

export class SuperTree extends BaseExpressionTree {
	constructor(public token: TokenOfType<Keyword.super>) {
		super();
	}

	referent: ClassDeclarationInfo | InterfaceDeclarationInfo | null = null;

	*iterTokensAndTrees(): Generator<Token | BaseTree> {
		yield this.token;
	}

	prettyPrint() {
		return "super";
	}
}

/**
 * Type cast, e.g. (int) -3 or (ArrayList<Integer>) o
 */
export class TypeCastTree extends BaseExpressionTree {
	constructor(
		public openParen: TokenOfType<Separator.LParen>,
		public type: ReferenceTypeTree | ArrayTypeTree | PrimitiveTypeTree,
		public boundsTree: BoundsTree | null,
		public closeParen: TokenOfType<Separator.RParen> | null,
		public stmt: BaseExpressionTree | null
	) {
		super();
	}

	*iterTokensAndTrees(): Generator<Token | BaseTree> {
		yield this.openParen;
		yield this.type;
		if (this.boundsTree) yield this.boundsTree;
		if (this.closeParen) yield this.closeParen;
		if (this.stmt) yield this.stmt;
	}

	prettyPrint() {
		return `(${this.type.prettyPrint()}) ${this.stmt?.prettyPrint()}`;
	}
}

/**
 * Reference type, e.g. ArrayList<Integer>
 * Visualisation of the components:
 *                  name
 * lhs             vvvvvvvvv
 * vvvvvvvvvvvvvvv           vvvvvvvvv typeArgs   vv brackets
 * Cowpox<Chicken>.ArrayList <Integer>            []
 *                ^
 *             dotToken
 */
export class ReferenceTypeTree extends BaseTree {
	constructor(
		public lhs: ReferenceTypeTree | null,
		public dotToken: Token | null,
		public identifier: IdentTree,
		public typeArgs: TypeArgumentsTree | null
	) {
		super();
	}

	public referent:
		| InterfaceDeclarationInfo
		| ClassDeclarationInfo
		| PackageInfo
		| TypeParameterInfo
		| null = null;

	*iterTokensAndTrees(): Generator<Token | BaseTree> {
		if (this.lhs) yield this.lhs;
		if (this.dotToken) yield this.dotToken;
		yield this.identifier;
		if (this.typeArgs) yield this.typeArgs;
	}

	prettyPrint(diamondArgs = false): string {
		const p = this.lhs?.prettyPrint();
		let s = p ? p + "." : "";
		s += this.identifier.prettyPrint();
		if (this.typeArgs) {
			s += diamondArgs ? "<>" : this.typeArgs.prettyPrint();
		}
		return s;
	}

	isTypeTree(): this is ReferenceTypeTree | PrimitiveTypeTree | ArrayTypeTree {
		return true;
	}
}

export class WildcardTree extends BaseTree {
	constructor(
		public lhs: TokenOfType<Operator.Question>,
		public bound: TokenOfType<Keyword.super | Keyword.extends> | null,
		public boundType:
			| ReferenceTypeTree
			| ArrayTypeTree
			| WildcardTree
			| PrimitiveTypeTree
			| null
	) {
		super();
	}

	public referent: TypeParameterInfo | null = null;

	*iterTokensAndTrees(): Generator<Token | BaseTree> {
		yield this.lhs;
		if (this.bound) yield this.bound;
		if (this.boundType) yield this.boundType;
	}

	boundKind(): "extends" | "super" | "none" {
		return !this.bound
			? "none"
			: this.bound.type === Keyword.super
				? "super"
				: "extends";
	}

	prettyPrint(): string {
		const {bound} = this;

		return `?${bound ? ` ${bound} ${this.boundType?.prettyPrint()}` : ""}`;
	}
}

/**
 * e.g. int[] in int[] a. If there are trailing brackets like int a[], they are part of the variable declaration tree,
 * where applicable, as this is part of the CST rather than the AST.
 */
export class ArrayTypeTree extends BaseTree {
	constructor(
		public type: PrimitiveTypeTree | ReferenceTypeTree | WildcardTree,
		public bracketsTree: BracketsTree
	) {
		super();
	}

	*iterTokensAndTrees(): Generator<Token | BaseTree> {
		yield this.type;
		if (this.bracketsTree) yield this.bracketsTree;
	}

	noTemplateArgsOrArray() {
		return false;
	}

	prettyPrint() {
		return this.type.prettyPrint() + this.bracketsTree?.prettyPrint();
	}

	isTypeTree(): this is ReferenceTypeTree | PrimitiveTypeTree | ArrayTypeTree {
		return true;
	}
}

export class EmptyStatementTree extends BaseTree {
	constructor(public semi: TokenOfType<Separator.Semicolon>) {
		super();
	}

	*iterTokensAndTrees(): Generator<Token | BaseTree> {
		yield this.semi;
	}
}

export class IfStatementTree extends BaseTree {
	constructor(
		public ifToken: TokenOfType<Keyword.if>,
		public openParen: TokenOfType<Separator.LParen> | null,
		public condition: BaseExpressionTree | null,
		public closeParen: TokenOfType<Separator.RParen> | null,
		public thenStatement: BaseTree | null,
		public elseToken: TokenOfType<Keyword.else> | null,
		public elseStatement: BaseTree | null
	) {
		super();
	}

	*iterTokensAndTrees(): Generator<Token | BaseTree> {
		yield this.ifToken;
		if (this.openParen) yield this.openParen;
		if (this.condition) yield this.condition;
		if (this.closeParen) yield this.closeParen;
		if (this.thenStatement) yield this.thenStatement;
		if (this.elseToken) yield this.elseToken;
		if (this.elseStatement) yield this.elseStatement;
	}
}

/**
 * Type arguments.
 *
 * Visualisation:
 * v open  v close
 * <Integer>
 *  ^^^^^^^ args[0]
 */
export class TypeArgumentsTree extends BaseTree {
	constructor(
		public open: TokenOfType<Operator.Lt>,
		public args: (
			| ReferenceTypeTree
			| ArrayTypeTree
			| PrimitiveTypeTree
			| WildcardTree
		)[],
		public commas: TokenOfType<Separator.Comma>[],
		public close: TokenOfType<Operator.Gt> | null
	) {
		super();
	}

	*iterTokensAndTrees(): Generator<Token | BaseTree> {
		yield this.open;
		for (const arg of this.args) yield arg;
		for (const comma of this.commas) yield comma;
		if (this.close) yield this.close;
	}

	prettyPrint() {
		let s = "<";
		const {args} = this;
		for (let i = 0; i < args.length; ++i) {
			s += args[i]!.prettyPrint();
			if (i !== args.length - 1) s += ", ";
		}
		s += ">";
		return s;
	}

	isEmpty() {
		return this.args.length === 0;
	}

	isClosed() {
		return this.close !== null;
	}
}

/**
 * Method invocation.
 *
 * Visualisation:
 *
 *               typeArguments                                 closeParen
 *   dotToken  v vvvvvvvvv                       args[1]  vvvv v
 * ArrayList   . <Integer>   cow        ( arg0          , arg1 )
 * ^^^^^^^^^                 ^^^        ^ ^^^^ args[0]  ^ comma
 *  expression             identifier  openParen
 *
 * or:
 *       openParen
 *         v
 * getX    (  )
 * ^^^^       ^ closeParen
 * identifier
 */
export class MethodInvocationTree extends BaseExpressionTree {
	constructor(
		public expression: BaseExpressionTree | null,
		public dotToken: Token | null,
		public ident:
			| IdentTree
			| SuperTree
			| ThisTree
			| ClassCreationTree
			| ArrayCreationTree
			| AnonymousClassCreationTree
			| null,
		public openParen: TokenOfType<Separator.LParen> | null,
		public args: BaseExpressionTree[],
		public commas: (Token | null)[], // should have one less than args (unless both are 0)
		public closeParen: TokenOfType<Separator.RParen> | null,
		public typeArguments: TypeArgumentsTree | null = null
	) {
		super();
	}

	referent: MethodDeclarationInfo | null = null;

	*iterTokensAndTrees(): Generator<Token | BaseTree> {
		if (this.expression) yield this.expression;
		if (this.dotToken) yield this.dotToken;
		if (this.ident) yield this.ident;
		if (this.openParen) yield this.openParen;
		for (const arg of this.args) yield arg;
		for (const comma of this.commas) if (comma) yield comma;
		if (this.closeParen) yield this.closeParen;
		if (this.typeArguments) yield this.typeArguments;
	}

	prettyPrint() {
		let lhs = this.expression?.prettyPrint();
		if (lhs === undefined) lhs = "";
		else lhs += ".";
		return `${lhs}${this.ident?.prettyPrint()}(${this.args.map((x) => x.prettyPrint()).join(", ")})`;
	}
}

/**
 * Field access, e.g. a.b
 */
export class MethodReferenceTree extends BaseExpressionTree {
	constructor(
		public expressionOrTy:
			| BaseExpressionTree
			| ArrayTypeTree
			| ReferenceTypeTree
			| null,
		public doubleColon: Token,
		public typeArgs: TypeArgumentsTree | null,
		public identifier: IdentTree | (TokenKeyword & {type: Keyword.new}) | null
	) {
		super();
	}

	*iterTokensAndTrees(): Generator<Token | BaseTree> {
		if (this.expressionOrTy) yield this.expressionOrTy;
		yield this.doubleColon;
		if (this.typeArgs) yield this.typeArgs;
		if (this.identifier) yield this.identifier;
	}
}

export class AnonymousClassCreationTree extends BaseExpressionTree {
	constructor(
		public newToken: Token,
		public type: ReferenceTypeTree | ArrayTypeTree | PrimitiveTypeTree | null,
		public typeArgs: TypeArgumentsTree | null,
		public args: BaseExpressionTree[],
		public body: BaseTree[] | null,
		public openBrace: TokenOfType<Separator.LBrace>,
		public closeBrace: TokenOfType<Separator.RBrace> | null
	) {
		super();
	}

	*iterTokensAndTrees(): Generator<Token | BaseTree> {
		yield this.newToken;
		if (this.type) yield this.type;
		if (this.typeArgs) yield this.typeArgs;
		for (const arg of this.args) yield arg;
		if (this.body) {
			yield this.openBrace;
			for (const b of this.body) yield b;
			if (this.closeBrace) yield this.closeBrace;
		}
	}
}

export class AssertTree extends BaseTree {
	constructor(
		public assertToken: TokenOfType<Keyword.assert>,
		public expr: BaseExpressionTree | null,
		public colon: TokenOfType<Operator.Colon> | null,
		public message: BaseExpressionTree | null,
		public semi: TokenOfType<Separator.Semicolon> | null
	) {
		super();
	}

	*iterTokensAndTrees(): Generator<Token | BaseTree> {
		yield this.assertToken;
		if (this.expr) yield this.expr;
		if (this.colon) yield this.colon;
		if (this.message) yield this.message;
		if (this.semi) yield this.semi;
	}
}
